// Client-side redaction. Diagnostics capture metadata/events, never content.
// This is the first line of defence (the server sanitizes again on ingest).
// PDPA 2025 makes biometrics/ID sensitive: the KYC consent does not cover
// shipping identity data into a debug channel, so we scrub aggressively here.

const SENSITIVE_KEY = /password|token|secret|authorization|apikey|api_key|nric|mykad|passport|idnumber|id_number|fullname|full_name|dob|birth|address|selfie|face|image|photo/i;
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const LONG_DIGITS_RE = /\b\d{6,}\b/g; // ID / passport-like runs

// Reduce a URL to origin + pathname (query strings can carry tokens / PII).
export function sanitizeUrl(url) {
  if (typeof url !== "string" || !url) return url || null;
  try {
    const u = new URL(url, window.location.origin);
    return u.origin + u.pathname;
  } catch (e) {
    // Not a full URL — strip anything after ? or #
    return url.split("?")[0].split("#")[0];
  }
}

export function scrubString(s) {
  let out = s.length > 1000 ? s.slice(0, 1000) + "...[TRUNC]" : s;
  out = out.replace(/\bhttps?:\/\/[^\s?#]+\?[^\s]*/gi, (m) => m.split("?")[0] + "?[stripped]");
  return out.replace(EMAIL_RE, "[email]").replace(LONG_DIGITS_RE, "[num]");
}

// Deep-sanitize arbitrary data: redact PII keys, scrub string values, drop
// functions/undefined (which JSON would drop anyway), cap depth and breadth.
export function sanitize(value, depth = 0) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return scrubString(value);
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return value;
  if (depth >= 4) return null;
  if (Array.isArray(value)) return value.slice(0, 50).map((v) => sanitize(v, depth + 1));
  if (typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value)) {
      if (SENSITIVE_KEY.test(k)) {
        out[k] = "[REDACTED]";
        continue;
      }
      out[k] = sanitize(value[k], depth + 1);
    }
    return out;
  }
  return null; // functions, symbols, bigint
}
